import { useState } from 'react';
import { FormInput, FormSelect } from '@components/UI';
import type { ComparisonOperator } from '@einhaus/project314-types';
import type {
    EditorConditionNode,
    ScreenerConditionValueType,
    ScreenerFeatureDefinition,
    ScreenerMissingPolicy
} from '../types/screenerEditor';
import { operatorOptions } from '../utils/screenerEditorUtils';

interface RuleConditionProps {
    node: EditorConditionNode;
    path: number[];
    features: readonly ScreenerFeatureDefinition[];
    featureOptions: { value: string; label: string }[];
    onUpdate: (path: number[], updater: (node: EditorConditionNode) => EditorConditionNode) => void;
    onRemove: (path: number[]) => void;
}

const RuleCondition = ({ node, path, features, featureOptions, onUpdate, onRemove }: RuleConditionProps) => {
    const [isAdvancedOpen, setIsAdvancedOpen] = useState(
        () => node.duration.trim().length > 0 || node.lookbackBars.trim().length > 0 || node.missingPolicy !== 'failClosed'
    );

    const feature = features.find((f) => f.key === node.featureKey);
    const isFeatureSupported = featureOptions.some((option) => option.value === node.featureKey);
    const selectableFeatures = isFeatureSupported
        ? featureOptions
        : [
              { value: node.featureKey, label: `${feature?.label ?? node.featureKey} (unsupported for selected entity types)` },
              ...featureOptions
          ];
    const allowedOperators = feature?.allowedOperators ?? [];
    const allowedValueTypes = feature?.inputValueTypes ?? [];

    const handleFieldChange = (patch: Partial<EditorConditionNode>) => {
        onUpdate(path, (n) => ({ ...n, ...patch }));
    };

    const handleFeatureChange = (nextKey: string) => {
        const nextFeature = features.find((f) => f.key === nextKey);
        const nextValueType = nextFeature?.inputValueTypes?.[0] ?? 'number';
        const nextOperator = nextFeature?.allowedOperators?.[0] ?? '>=';

        onUpdate(path, (n) => ({
            ...n,
            featureKey: nextKey,
            valueType: nextValueType,
            operator: nextOperator,
            value: '',
            values: ''
        }));
    };

    const filteredOperators = operatorOptions
        .filter((o) => allowedOperators.includes(o.value))
        .map((o) => ({ value: o.value, label: o.label }));

    const renderValueInput = () => {
        if (node.valueType === 'boolean') {
            return (
                <FormSelect
                    noMargin
                    options={[
                        { value: 'true', label: 'true' },
                        { value: 'false', label: 'false' }
                    ]}
                    size="sm"
                    value={node.value}
                    onChange={(e) => handleFieldChange({ value: e.target.value })}
                />
            );
        }

        if (node.valueType === 'numberList' || node.valueType === 'stringList' || node.valueType === 'enumList') {
            return (
                <FormInput
                    noMargin
                    placeholder="comma-separated"
                    value={node.values}
                    onChange={(e) => handleFieldChange({ values: e.target.value })}
                />
            );
        }

        return <FormInput noMargin placeholder="Value" value={node.value} onChange={(e) => handleFieldChange({ value: e.target.value })} />;
    };

    return (
        <div>
            <div className="flex items-center gap-2 py-2 px-3 bg-zinc-900/40 border border-zinc-700/50 rounded-lg group hover:border-zinc-600 transition-colors">
                <div className="min-w-[200px] flex-1">
                    <FormSelect
                        noMargin
                        options={selectableFeatures}
                        size="sm"
                        value={node.featureKey}
                        onChange={(e) => handleFeatureChange(e.target.value)}
                    />
                </div>

                <div className="min-w-[110px]">
                    <FormSelect
                        noMargin
                        options={filteredOperators}
                        size="sm"
                        value={node.operator}
                        onChange={(e) => handleFieldChange({ operator: e.target.value as ComparisonOperator })}
                    />
                </div>

                {allowedValueTypes.length > 1 && (
                    <div className="min-w-[100px]">
                        <FormSelect
                            noMargin
                            options={allowedValueTypes.map((v) => ({ value: v, label: v }))}
                            size="sm"
                            value={node.valueType}
                            onChange={(e) =>
                                handleFieldChange({ valueType: e.target.value as ScreenerConditionValueType, value: '', values: '' })
                            }
                        />
                    </div>
                )}

                <div className="min-w-[120px] flex-1">{renderValueInput()}</div>

                <button
                    className="p-1.5 text-zinc-400 hover:text-zinc-200 transition-colors"
                    title="Advanced options"
                    type="button"
                    onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
                >
                    <svg
                        className={`w-4 h-4 ${isAdvancedOpen ? 'text-blue-400' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                        />
                        <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                    </svg>
                </button>

                <button
                    className="p-1.5 text-zinc-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                    title="Remove condition"
                    type="button"
                    onClick={() => onRemove(path)}
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                    </svg>
                </button>
            </div>

            {isAdvancedOpen && (
                <div className="flex gap-3 pl-4 pr-10 py-2 ml-3 border-l border-zinc-700/50">
                    <div className="flex-1">
                        <FormInput
                            noMargin
                            label="Duration"
                            placeholder="e.g. 5d, 1h"
                            value={node.duration}
                            onChange={(e) => handleFieldChange({ duration: e.target.value })}
                        />
                    </div>
                    <div className="flex-1">
                        <FormInput
                            noMargin
                            label="Lookback Bars"
                            placeholder="integer"
                            value={node.lookbackBars}
                            onChange={(e) => handleFieldChange({ lookbackBars: e.target.value })}
                        />
                    </div>
                    <div className="flex-1">
                        <FormSelect
                            noMargin
                            label="Missing Policy"
                            options={[
                                { value: 'failClosed', label: 'Fail Closed' },
                                { value: 'ignoreCondition', label: 'Ignore Condition' }
                            ]}
                            size="sm"
                            value={node.missingPolicy}
                            onChange={(e) => handleFieldChange({ missingPolicy: e.target.value as ScreenerMissingPolicy })}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default RuleCondition;
